import { useSelector, useDispatch } from 'react-redux';
import { CloseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Flex, Typography, Select, Button, Empty } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { setSelectedStep, setWorkflow, setChangeDetected } from '../../../redux/slices/connectorSlice';

// Side panel for configuring the completion rule of a merge (join) node:
// which incoming parallel branches are mandatory, and how optional branches
// are grouped (e.g. "any one of N3/N4 approved").
const JoinRuleForm = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { selectedStep, workflow } = useSelector(state => state.connector);
    const isJoin = selectedStep?.data?.type === 'join';

    const joinNode = useMemo(
        () => workflow?.nodes?.find(n => n.id === selectedStep?.id),
        [workflow, selectedStep]
    );

    // Branches currently merged into this join, resolved from incoming edges.
    // Prefer the actual step name (the node feeding into the join) over a generic label.
    const incomingBranches = useMemo(() => {
        if (!joinNode) return [];
        return (workflow?.edges || [])
            .filter(e => e.target === joinNode.id)
            .map((e, i) => {
                const sourceNode = (workflow?.nodes || []).find(n => n.id === e.source);
                const stepName = sourceNode?.data?.name;
                return {
                    branchId: e.data?.branchId || e.id,
                    label: stepName || e.data?.branchLabel || `${t('Branch')} ${i + 1}`,
                };
            });
    }, [workflow, joinNode, t]);

    const [branches, setBranches] = useState([]);
    const [optionalGroups, setOptionalGroups] = useState([]);

    useEffect(() => {
        if (!isJoin || !joinNode) return;
        const existing = joinNode.data?.joinRule || {};
        const existingBranches = existing.branches || [];
        const merged = incomingBranches.map(ib => {
            const prev = existingBranches.find(b => b.branchId === ib.branchId);
            return {
                branchId: ib.branchId,
                label: ib.label,
                requirement: prev?.requirement || 'mandatory',
                optionalGroup: prev?.optionalGroup || null,
            };
        });
        setBranches(merged);
        setOptionalGroups(existing.optionalGroups || []);
    }, [isJoin, joinNode, incomingBranches]);

    if (!isJoin) return null;

    const close = () => dispatch(setSelectedStep({}));

    const updateBranch = (branchId, patch) =>
        setBranches(prev => prev.map(b => (b.branchId === branchId ? { ...b, ...patch } : b)));

    const addGroup = () =>
        setOptionalGroups(prev => [
            ...prev,
            { groupId: uuidv4(), label: `${t('Group')} ${prev.length + 1}`, mode: 'any' },
        ]);

    const removeGroup = (groupId) => {
        setOptionalGroups(prev => prev.filter(g => g.groupId !== groupId));
        setBranches(prev => prev.map(b => (b.optionalGroup === groupId ? { ...b, requirement: 'mandatory', optionalGroup: null } : b)));
    };

    // Assign/unassign branches to a group directly. Selected branches become optional members
    // of the group; branches removed from it revert to mandatory.
    const assignBranchesToGroup = (groupId, selectedBranchIds) => {
        setBranches(prev => prev.map(b => {
            if (selectedBranchIds.includes(b.branchId)) {
                return { ...b, requirement: 'optional', optionalGroup: groupId };
            }
            if (b.optionalGroup === groupId) {
                return { ...b, requirement: 'mandatory', optionalGroup: null };
            }
            return b;
        }));
    };

    const save = () => {
        // Mandatory branches never belong to an optional group; drop empty groups.
        const cleanedBranches = branches.map(b =>
            b.requirement === 'mandatory' ? { ...b, optionalGroup: null } : b
        );
        const usedGroupIds = new Set(
            cleanedBranches.filter(b => b.requirement === 'optional' && b.optionalGroup).map(b => b.optionalGroup)
        );
        const cleanedGroups = optionalGroups.filter(g => usedGroupIds.has(g.groupId));
        const joinRule = { branches: cleanedBranches, optionalGroups: cleanedGroups };
        const updatedNodes = workflow.nodes.map(n =>
            n.id === joinNode.id ? { ...n, data: { ...n.data, joinRule } } : { ...n }
        );
        dispatch(setWorkflow({ ...workflow, nodes: updatedNodes }));
        dispatch(setChangeDetected(true));
        dispatch(setSelectedStep({}));
    };

    return (
        <div className="step-details-container" id="join-rule-container" style={{ display: 'flex' }}>
            <Flex className="step-details-header" align="center" justify="space-between">
                <Typography.Title level={5} className="margin-0">{t('Merge Rule')}</Typography.Title>
                <CloseOutlined className="icon-medium" onClick={close} />
            </Flex>
            <Flex vertical justify="space-between" style={{ height: '100%' }}>
                <div className="step-details-body">
                    <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                        {t('All mandatory branches must complete. Each optional group proceeds once its rule is met.')}
                    </Typography.Paragraph>

                    {branches.length === 0 ? (
                        <Empty description={t('No branches merged into this node yet')} />
                    ) : null}

                    <Flex vertical gap={12}>
                        {/* Mandatory branches, and optional branches not yet assigned to a group */}
                        {branches.filter(b => !(b.requirement === 'optional' && b.optionalGroup)).map(b => (
                            <div key={b.branchId} style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                                <Flex justify="space-between" align="center" style={{ marginBottom: b.requirement === 'optional' ? 8 : 0 }}>
                                    <Typography.Text strong>{b.label}</Typography.Text>
                                    <Select
                                        size="small"
                                        style={{ width: 130 }}
                                        value={b.requirement}
                                        onChange={(v) => updateBranch(b.branchId, { requirement: v, optionalGroup: v === 'mandatory' ? null : b.optionalGroup })}
                                        options={[
                                            { label: t('Mandatory'), value: 'mandatory' },
                                            { label: t('Optional'), value: 'optional' },
                                        ]}
                                    />
                                </Flex>
                                {b.requirement === 'optional' ? (
                                    <Flex align="center" gap={8}>
                                        <span style={{ fontSize: 12, color: '#888' }}>{t('Group')}</span>
                                        <Select
                                            size="small"
                                            style={{ flex: 1 }}
                                            placeholder={t('Select group')}
                                            value={b.optionalGroup || undefined}
                                            onChange={(v) => updateBranch(b.branchId, { optionalGroup: v })}
                                            options={optionalGroups.map(g => ({ label: g.label, value: g.groupId }))}
                                            notFoundContent={t('Add a group first')}
                                        />
                                    </Flex>
                                ) : null}
                            </div>
                        ))}

                        {/* One card per optional group, holding all its member branches together */}
                        {optionalGroups.map(g => {
                            const members = branches.filter(b => b.optionalGroup === g.groupId);
                            return (
                                <div key={g.groupId} style={{ border: '1px solid #d6d6f5', background: '#fafaff', borderRadius: 8, padding: 12 }}>
                                    <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                                        <Typography.Text strong>{g.label}</Typography.Text>
                                        <DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} onClick={() => removeGroup(g.groupId)} />
                                    </Flex>

                                    <Flex vertical gap={2} style={{ marginBottom: 8 }}>
                                        {members.length === 0 ? (
                                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('No branches in this group yet')}</Typography.Text>
                                        ) : (
                                            members.map(m => (
                                                <Typography.Text key={m.branchId} style={{ fontSize: 13 }}>• {m.label}</Typography.Text>
                                            ))
                                        )}
                                    </Flex>

                                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                                        {t('Proceeds when any one completes')}
                                    </Typography.Text>

                                    <Select
                                        mode="multiple"
                                        size="small"
                                        allowClear
                                        style={{ width: '100%' }}
                                        placeholder={t('Add or remove branches')}
                                        value={members.map(b => b.branchId)}
                                        onChange={(ids) => assignBranchesToGroup(g.groupId, ids)}
                                        options={branches.map(b => ({ label: b.label, value: b.branchId }))}
                                    />
                                </div>
                            );
                        })}
                    </Flex>

                    <Flex justify="flex-end" style={{ marginTop: 12 }}>
                        <Button size="small" icon={<PlusOutlined />} onClick={addGroup}>{t('Add group')}</Button>
                    </Flex>
                </div>
                <div className="step-details-action">
                    <Flex gap={8} justify="flex-end">
                        <Button onClick={close}>{t('Cancel')}</Button>
                        <Button type="primary" onClick={save}>{t('Save')}</Button>
                    </Flex>
                </div>
            </Flex>
        </div>
    );
};

export default JoinRuleForm;
